/**
 * 数据增强器管理器
 * 职责：管理所有数据增强器，按优先级顺序执行
 */

import { ButtonSourceEnricher } from "./ButtonSourceEnricher";
import { DOMEnricher } from "./DOMEnricher";
import { MaterialSourceEnricher } from "./MaterialSourceEnricher";
import { NavigationEnricher } from "./NavigationEnricher";
import { PinDetailEnricher } from "./PinDetailEnricher";
import { SearchEnricher } from "./SearchEnricher";
import { SearchEventEnricher } from "./SearchEventEnricher";
import { DataEnricher, EnrichContext, EnrichOptions } from "./types";
import { URLEnricher } from "./URLEnricher";

export class EnricherManager {
  private enrichers: DataEnricher[] = [];

  /**
   * 注册数据增强器
   */
  register(enricher: DataEnricher): void {
    this.enrichers.push(enricher);
    // 按优先级排序（数字越小优先级越高）
    this.enrichers.sort((a, b) => a.priority - b.priority);

    if (process.env.NODE_ENV === "development") {
      console.log(
        `[EnricherManager] Registered enricher: ${enricher.name} (priority: ${enricher.priority})`
      );
    }
  }

  /**
   * 执行数据增强
   */
  enrich(
    trackData: Record<string, any>,
    targetNode?: Element | null,
    page?: { page_nm: string; sub_page_nm?: string },
    options?: EnrichOptions
  ): Record<string, any> {
    // 如果没有提供 page 信息，尝试从 getPage 获取
    let pageData = page;
    if (!pageData) {
      // 尝试从现有 tracker 获取页面信息
      try {
        if (typeof window !== "undefined" && (window as any).getPage) {
          pageData = (window as any).getPage();
        }
      } catch (e) {
        // 降级处理
        pageData = { page_nm: "", sub_page_nm: "" };
      }
    }

    const context: EnrichContext = {
      targetNode,
      page: pageData || { page_nm: "", sub_page_nm: "" },
      trackData: { ...trackData },
      options,
    };

    // 按优先级顺序执行增强器
    this.enrichers.forEach((enricher) => {
      if (enricher.shouldEnrich(context)) {
        try {
          enricher.enrich(context);

          if (process.env.NODE_ENV === "development") {
            console.log(`[EnricherManager] Executed: ${enricher.name}`);
          }
        } catch (error) {
          console.error(`[EnricherManager] Error in ${enricher.name}:`, error);
        }
      }
    });

    return context.trackData;
  }

  /**
   * 获取所有增强器
   */
  getEnrichers(): DataEnricher[] {
    return [...this.enrichers];
  }

  /**
   * 清除所有增强器
   */
  clear(): void {
    this.enrichers = [];
  }
}

// 创建单例并注册默认增强器
export const enricherManager = new EnricherManager();

// 注册默认增强器（按优先级顺序）
enricherManager.register(new NavigationEnricher()); // Priority 1 - 导航信息（所有埋点都需要）
enricherManager.register(new DOMEnricher()); // Priority 2 - DOM 数据
enricherManager.register(new ButtonSourceEnricher()); // Priority 3 - 按钮来源（button_click 事件）
enricherManager.register(new MaterialSourceEnricher()); // Priority 3 - 素材来源（material_click 和 content_expose 事件）
enricherManager.register(new SearchEnricher()); // Priority 3 - 搜索上下文
enricherManager.register(new SearchEventEnricher()); // Priority 5 - 搜索事件（search 事件，在 SearchEnricher 之后执行，确保空字段不会被覆盖）
enricherManager.register(new PinDetailEnricher()); // Priority 5 - 详情页上下文
enricherManager.register(new URLEnricher()); // Priority 6 - URL 参数（兜底）

if (process.env.NODE_ENV === "development") {
  console.log("[EnricherManager] Default enrichers registered");
}
